/**
 * Merging two versions of a day. FR-506, data model Section 6.
 *
 * Pure, and deliberately here rather than in the transport: this is the part where
 * being wrong loses somebody's data, and it should be testable without a relay, a
 * network, or a second device.
 *
 * ## Three rules, and why each is what it is
 *
 * **Scalar fields merge per field.** Two devices editing different fields of the
 * same day must both survive — logging pain on a phone while adding energy on a
 * laptop is normal, and a whole-record last-write-wins would silently discard one.
 *
 * **Set fields do not.** `symptoms` and `tags` take the later set wholesale. A
 * union looks kinder and is wrong: removing a symptom is a deliberate correction,
 * and a union merge resurrects it on the next sync. A user who un-ticks "nausea"
 * and watches it come back has been told the app does not believe them.
 *
 * **Notes are never discarded.** A conflicting note is kept as a variant rather
 * than overwritten. Every other field is a value the user picked from a small set,
 * so losing the older one costs a tap; a note is the user's own words, and
 * silently dropping a sentence someone wrote about a hard day is the least
 * acceptable thing this file could do.
 *
 * ## The clock guard
 *
 * Device clocks are wrong more often than intuition suggests — a phone with a bad
 * timezone, a laptop that was off for a week, a deliberately-set clock. So a
 * timestamp implausibly far in the future is not trusted for ordering, and exact
 * ties fall back to comparing device ids so that every device reaches the *same*
 * answer. Convergence matters more than being right about which edit came first:
 * two devices that pick different winners never stop disagreeing.
 */
import type { DayEntry, Timestamp } from './types'

/**
 * How far ahead of local time a remote timestamp may be and still be trusted.
 *
 * Five minutes covers ordinary clock drift and NTP lag. Beyond that the remote
 * clock is wrong or set deliberately, and treating it as authoritative would let
 * one bad device win every conflict for ever.
 */
export const MAX_CLOCK_SKEW_MS = 5 * 60 * 1000

/** Fields merged individually, using per-field timestamps when present. */
const SCALAR_FIELDS = [
  'flow',
  'pain',
  'mood',
  'energy',
  'sleep',
  'water',
  'meds',
  'custom',
] as const

/** Taken as a whole from the later version. See the header on why not union. */
const SET_FIELDS = ['symptoms', 'tags'] as const

export interface MergeOptions {
  /** Local wall clock, passed in so this stays pure and testable. */
  now: Timestamp
}

export interface MergeResult {
  entry: DayEntry
  /** True when both sides had changed something — useful for status reporting. */
  converged: boolean
  /** Notes kept as variants because they conflicted. */
  variants: string[]
}

/** The effective timestamp for one field, falling back to the record's. */
function fieldTime(entry: DayEntry, field: string): Timestamp {
  return entry.meta.fieldTimes?.[field] ?? entry.meta.updatedAt
}

/** Whether this version claims to have touched the field specifically. */
const touched = (entry: DayEntry, field: string): boolean =>
  entry.meta.fieldTimes?.[field] !== undefined

/**
 * Structural equality for a field value.
 *
 * Cheap and sufficient: field values are scalars, small arrays, or flat objects.
 * Needed so that fields nobody changed are not treated as conflicts — see
 * `sameValue` usage in `resolveField`.
 */
function sameValue(a: unknown, b: unknown): boolean {
  if (a === b) return true
  if (a === undefined || b === undefined) return false
  return JSON.stringify(a) === JSON.stringify(b)
}

/**
 * Whether `remote` should be trusted as later than `local` for this field.
 *
 * ## A side that never touched a field has no claim on it
 *
 * This is the subtlest rule in the module, and getting half of it right produced
 * the worst bug found while writing it.
 *
 * A version that never touched a field carries no per-field timestamp for it, so
 * `fieldTime` falls back to the record's `updatedAt`. Used naively that makes a
 * device which merely edited *something else* look as though it had just acted on
 * every field it never mentioned. In one direction that silently *deletes* data;
 * in the other it silently *blocks* a real edit from the other device. The second
 * is worse than it sounds: the two devices then reach different answers and never
 * converge, which no amount of re-syncing repairs.
 *
 * So claims are compared, not timestamps:
 *
 *  - only remote touched it -> remote wins
 *  - only local touched it  -> local wins
 *  - both touched it        -> compare per-field times
 *  - neither touched it     -> compare record times, which is correct
 *                             whole-record last-write-wins for records written
 *                             before sync existed and which have no field times
 */
function remoteWins(
  local: DayEntry,
  remote: DayEntry,
  field: string,
  now: Timestamp,
): boolean {
  const remoteAt = fieldTime(remote, field)

  // The clock guard, applied before anything else. A remote timestamp far in the
  // future is not evidence of recency; treating it as such would let one
  // misconfigured device win every conflict for ever.
  if (remoteAt > now + MAX_CLOCK_SKEW_MS) return false

  const remoteTouched = touched(remote, field)
  const localTouched = touched(local, field)

  if (remoteTouched !== localTouched) return remoteTouched

  const localAt = fieldTime(local, field)
  if (remoteAt !== localAt) return remoteAt > localAt

  // Exact tie. Compare device ids so every device reaches the SAME answer —
  // convergence matters more than being right about which edit came first,
  // because two devices picking different winners never stop disagreeing.
  return remote.meta.updatedBy > local.meta.updatedBy
}

/**
 * Apply one field, reporting whether anything actually moved.
 *
 * The equality check first is not an optimisation. Without it, a tie on two
 * fields that are both absent resolves by device id and reports a change — so
 * every sync between two idle devices would claim to have converged something,
 * and the status display would never settle.
 */
function resolveField(
  merged: DayEntry,
  fieldTimes: Record<string, Timestamp>,
  local: DayEntry,
  remote: DayEntry,
  field: string,
  now: Timestamp,
  clone: (value: unknown) => unknown = (value) => value,
): boolean {
  const localValue = local[field as keyof DayEntry]
  const remoteValue = remote[field as keyof DayEntry]

  if (sameValue(localValue, remoteValue)) {
    if (fieldTimes[field] === undefined && touched(local, field)) {
      fieldTimes[field] = fieldTime(local, field)
    }
    return false
  }

  if (remoteWins(local, remote, field, now)) {
    if (remoteValue === undefined) delete merged[field as keyof DayEntry]
    else Object.assign(merged, { [field]: clone(remoteValue) })
    fieldTimes[field] = fieldTime(remote, field)
    return true
  }

  if (fieldTimes[field] === undefined && touched(local, field)) {
    fieldTimes[field] = fieldTime(local, field)
  }
  return false
}

/**
 * Merge a remote version of a day into the local one.
 *
 * Neither argument is mutated. The result carries the union of per-field
 * timestamps so a third device merging later makes the same decisions.
 */
export function mergeEntries(
  local: DayEntry,
  remote: DayEntry,
  options: MergeOptions,
): MergeResult {
  if (local.date !== remote.date) {
    throw new Error(`Refusing to merge different days: ${local.date} and ${remote.date}`)
  }

  const merged: DayEntry = {
    ...local,
    meta: { ...local.meta, fieldTimes: { ...local.meta.fieldTimes } },
  }
  const fieldTimes = merged.meta.fieldTimes as Record<string, Timestamp>
  let changed = false

  for (const field of SCALAR_FIELDS) {
    if (resolveField(merged, fieldTimes, local, remote, field, options.now)) changed = true
  }

  for (const field of SET_FIELDS) {
    // Copied, so the merged record never aliases the remote array.
    const copy = (value: unknown) => [...(value as unknown[])]
    if (resolveField(merged, fieldTimes, local, remote, field, options.now, copy)) {
      changed = true
    }
  }

  const noteResult = mergeNotes(local, remote, options.now)
  if (noteResult.note === undefined) delete merged.note
  else merged.note = noteResult.note
  if (noteResult.changed) changed = true
  if (touched(local, 'note') || touched(remote, 'note')) {
    fieldTimes.note = Math.max(fieldTime(local, 'note'), fieldTime(remote, 'note'))
  }

  merged.meta.updatedAt = Math.max(local.meta.updatedAt, remote.meta.updatedAt)
  merged.meta.createdAt = Math.min(local.meta.createdAt, remote.meta.createdAt)

  return { entry: merged, converged: changed, variants: noteResult.variants }
}

/**
 * Notes: keep both when they genuinely conflict.
 *
 * "Conflict" means both sides have text and neither is an extension of the other.
 * The prefix check matters because the ordinary case is one device having typed
 * more of the same sentence, and reporting that as a conflict would nag the user
 * about their own uninterrupted train of thought.
 */
function mergeNotes(
  local: DayEntry,
  remote: DayEntry,
  now: Timestamp,
): { note: string | undefined; variants: string[]; changed: boolean } {
  const a = local.note
  const b = remote.note

  if (a === b) return { note: a, variants: [], changed: false }
  if (a === undefined) return { note: b, variants: [], changed: true }
  if (b === undefined) {
    // A remote deletion of a note does NOT win by default. Deleting text is
    // easily accidental, and the asymmetry is deliberate: the cost of keeping a
    // note the user meant to remove is one more tap, and the cost of the reverse
    // is words they cannot get back.
    return { note: a, variants: [], changed: false }
  }

  if (b.startsWith(a)) return { note: b, variants: [], changed: true }
  if (a.startsWith(b)) return { note: a, variants: [], changed: false }

  // Genuinely divergent text. The later one is shown and the other is kept as a
  // variant for the user to resolve — never dropped.
  const remoteLater = remoteWins(local, remote, 'note', now)
  return remoteLater
    ? { note: b, variants: [a], changed: true }
    : { note: a, variants: [b], changed: true }
}

/**
 * Whether a tombstone should win over an entry.
 *
 * A deletion loses to any later edit: re-logging a day after deleting it is a
 * clear intention, and letting a stale tombstone erase it would be the same
 * mistake as a union merge on symptoms, in the other direction.
 */
export function deletionWins(
  tombstoneAt: Timestamp,
  entry: DayEntry,
  now: Timestamp,
): boolean {
  if (tombstoneAt > now + MAX_CLOCK_SKEW_MS) return false
  return tombstoneAt > entry.meta.updatedAt
}
